import buffer from '@turf/buffer'
import pointOnFeature from '@turf/point-on-feature'
import $ from 'jquery'
import { cloneDeep, isEmpty } from 'lodash'
import forOwn from 'lodash/forOwn'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import remove from 'lodash/remove'
import { getCenter } from 'ol/extent'
import OLFeature from 'ol/Feature'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import { stripHtml } from 'string-strip-html'

import { CONFIG_DRAWER_TAB_ACTIVE_SEARCH } from 'api/actions'
import PoziAPIData from 'api/data'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import ApplyTransformers from 'config/ApplyTransformers'
import { geoJsonToFeatureCollection } from 'config/LoaderHandleGeoJSON'
import GetPropertiesId from 'data/GetPropertiesId'
import GetPropertiesIdKey from 'data/GetPropertiesIdKey'
import PoziDrawerTabInfo from 'drawer/tabs/info/info'
import { Label } from 'drawer/tabs/info/Label'
import { ShowWhatsHere } from 'drawer/tabs/info/WhatsHereUtils'
import { setLayerSelected } from 'drawer/tabs/layers/actions'
import PoziMap from 'map/map'
import selectVectorFeature from 'map/SelectVectorFeature'
import PoziZoom from 'map/zoom'
import { DEFAULT_PROJECTION_CODE } from 'utils/constants'
import PoziNotifications from 'utils/notifications'
import { proj4, supportedProjections, isSupportedProjection } from 'utils/ProjUtils'
import { buildIntuitiveSearchResultSorterBy } from 'utils/sort'

import loadAndActivateVectorLayer from './loadAndActivateVectorLayer'

// Regexp for splitting string into words when there is whitespace, a slash, dot, comma or a hyphen
const splitIntoWordsRegexp = /[\s\\/.,-]+/

const SEARCH_MAX_WFS_FEATURES = import.meta.env.VITE_SEARCH_MAX_WFS_FEATURES

// Remove any potential HTML from a string and trim it
export const cleanupString = (str) => {
  if (isNil(str) || (typeof str !== 'string' && typeof str !== 'number')) {
    // console.log('cleanupString - invalid string', str, typeof str)

    return ''
  }

  // Remove any potential HTML from the string and trim it.
  // See: https://codsen.com/os/string-strip-html/
  const strCleaned = stripHtml(String(str)).result

  // console.debug('cleanupString - strCleaned', str, strCleaned)

  return strCleaned
}

export const matchQuery = (value, query) => {
  // More advanced search: every word in the query must be in the string as the start of
  // a word anywhere in that string
  const queryParts = query.split(splitIntoWordsRegexp).map((word) => word.toLowerCase())

  // Skip empty fields
  if (isNil(value)) {
    return false
  }

  // Convert all values to string, strip any HTML and trim whitespace
  const stringValue = cleanupString(value) || ''

  // Skip empty strings
  if (!stringValue) {
    return false
  }

  // Semi-smart string match: every word in the query must be in the string
  // but can be in different parts of that string
  const stringValueLC = stringValue.toLowerCase()
  // const match = query.split(stringValueRegexp).every(part => stringValueLC.includes(part))

  // Split string into words
  const stringValueWords = stringValueLC.split(splitIntoWordsRegexp)
  // console.debug('stringValueWords', stringValueWords, queryParts)

  // Every word in the query must be somewhere in the string.
  // i.e. 're ba' and 'ed ron' will match 'red iron bark'
  const match = queryParts.every((queryPart) => stringValueWords.some((word) => word.includes(queryPart)))

  return match
}

// This is where the search results get stored to avoid unnessary queries being sent
const resultsCache = {}

/**
 * Pozi API Adapter
 */
const PoziAPISearch = memoize(function () {
  // Adapters
  const adapters = {
    init: function () {
      return init()
    },
    getResults: function (suggestion, isWidget) {
      return getResults(suggestion, isWidget)
    },
    setSearchLayer: function (layer) {
      return setSearchLayer(layer)
    },
    search: function () {
      return search()
    }
  }

  const app = PoziUtils.getApp()

  const onZoomComplete = function (suggestion) {
    const layerConf = Model.get('app.search.layer', null)
    const titleid = layerConf?.titleid

    console.debug('onZoomComplete', titleid, suggestion)

    if (layerConf) {
      const layerEnabled = layerConf?.search?.enableLayer ?? true
      const searchFieldsLayer = layerConf?.search?.type === 'pozisearchfields'
      const layerInLayerControl = layerConf?.showInLayerControl ?? true
      let layerIsVisible = layerConf?.selected ?? false

      // If the search.enableLayer switch is not false, and the layer is not a searchFields layer that is turned off in the layer control, try to turn the layer on
      if (layerEnabled && (!searchFieldsLayer || (searchFieldsLayer && layerInLayerControl))) {
        layerIsVisible = PoziUtils.turnLayerOn(titleid)
      }

      // console.debug('onZoomComplete - layerinfo', titleid, layerEnabled, searchFieldsLayer, layerInLayerControl, layerIsVisible, layerConf)

      // If the layer is not visible and not a searchFields layer perform a What's Here search
      if (!layerIsVisible && !searchFieldsLayer) {
        console.log('PoziAPISearch - onZoomComplete - no layer... Add marker and trigger map selection.')

        app.selectMarker.setPosition(app.transformFrom4326(suggestion.xy)) // Triggers PoziMap().onChangePosition

        return PoziMap().showMarker()
      } else if (!searchFieldsLayer) {
        window.store.dispatch(setLayerSelected(titleid))
      }

      const observerId = Model.on('app.config.layerbytitleid[' + titleid + '].loaded', function (value) {
        console.debug('PoziAPISearch - onZoomComplete - layer loaded', titleid)

        Model.un('app.config.layerbytitleid[' + titleid + '].loaded', observerId)

        selectFeature(layerConf, suggestion)
      })

      const layerIsLoaded = Model.get('app.config.layerbytitleid[' + titleid + '].loaded', false)
      // Select feature if the layer is loaded, or the layer has been specifically configured as a hidden search layer using the searchFields keyword
      if (layerIsLoaded || suggestion.feature) {
        selectFeature(layerConf, suggestion) // Otherwise it should run on the .loaded listener above.
      }
    }
  }

  const searchComplete = (suggestion) => {
    console.debug('PoziAPISearch -searchComplete', suggestion)

    const maxZoom = 18
    if (suggestion.bbox) {
      PoziZoom().zoomToExtent(suggestion.bbox, maxZoom, () => onZoomComplete(suggestion))
    } else {
      PoziZoom().zoomToTarget(suggestion.xy, maxZoom, () => onZoomComplete(suggestion))
    }
  }

  const selectFeature = function (layerConf, suggestion) {
    // console.debug('PoziAPISearch - selectFeature', layerConf.titleid, suggestion)

    const layerIsLoaded = Model.get('app.config.layerbytitleid[' + layerConf.titleid + '].loaded', false)
    if (layerIsLoaded) {
      const idField = suggestion.idField
      const idText = suggestion.id || suggestion.value
      const featureFound = PoziUtils.selectFeatureInSearchLayer(idText, idField)
      if (featureFound) {
        PoziMap().removeMarker()
        console.log('PoziAPISearch - Vector feature selected. Skipping set marker position.')
      } else {
        console.log('PoziAPISearch().selectFeature: No feature found... Add marker and trigger map selection.')

        app.selectMarker.setPosition(app.transformFrom4326(suggestion.xy)) // Triggers PoziMap().onChangePosition

        PoziMap().showMarker()
      }
    } else if (suggestion.feature) {
      // Case where search is configured on a hidden layer using the searchFields keyword

      // Check if the searchFields keyword is configured to return a What's Here result
      const isSearchWhatsHere = layerConf.search?.whatsHere === 'true'
      if (isSearchWhatsHere) {
        const suggestionFeatureCollection = geoJsonToFeatureCollection(suggestion.feature, layerConf)
        // console.debug('PoziAPISearch - selectFeature - suggestionFeatureCollection', suggestionFeatureCollection)
        ShowWhatsHere(suggestionFeatureCollection)
        return
      }

      // Otherwise, just select the feature in the corresponding layer
      const olFeature = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(suggestion.feature)[0]
      console.debug('PoziAPISearch - selectFeature - olFeature', olFeature)
      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(layerConf.titleid)

      selectVectorFeature(olFeature, olLayer, true, true)
    } else {
      console.error('Search attempted on layer that is not loaded', layerConf)
    }
  }

  const getResults = async (suggestion, isWidget) => {
    // Sometimes the suggestion is null
    if (!suggestion) {
      console.log('getResults - Something went wrong with the suggestion', suggestion)
      return
    }

    // Handle case where suggestion hasn't been resolved yet and is dependent
    // on a (potentially async) function call (MapShare addresses for example)
    if (typeof suggestion === 'function') {
      // TODO: (rdw) Find a subtle way of letting user know something is happening
      //       that they may have to wait for (like a cursor change to 'busy')
      try {
        suggestion = await suggestion()
      } catch (error) {
        console.warn('Error in calling forward search function', error)
        PoziNotifications().warning('Something went wrong... Unable to select search item.')
        return
      }
    }

    // Somehow sometimes the suggestion is a string instead of an object
    if (typeof suggestion !== 'object') {
      console.log('getResults - Something went wrong with the suggestion', suggestion)
      PoziNotifications().warning('Unable to select search item. Did you happen to hit enter too quickly maybe?')
      return
    }

    const searchLayer = Model.get('app.search.layer', null)

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: searchLayer?.titleid || 'unknown_layer',
      action: 'Search Select Result'
    })

    // console.debug('getResults - feature', searchLayer.titleid, suggestion.id, suggestion.value)
    Model.setState('feature', searchLayer.titleid, suggestion.id || suggestion.value)

    Model.set('app.search.suggestion', suggestion.value)

    if (!suggestion && !suggestion.xy) {
      Model.set('app.search.lng', null)
      Model.set('app.search.lat', null)
    } else {
      Model.set('app.search.lng', suggestion.xy[0])
      Model.set('app.search.lat', suggestion.xy[1])
    }

    // Before the search is complete, clear the info panel, but only if not a widget
    if (!isWidget) {
      PoziDrawerTabInfo().clearState()
      PoziDrawerTabInfo().hide()
    }

    Model.set('app.search.complete', suggestion)

    // All done
    // ts = 0
    return true
  }

  /**
   * Init events
   */
  const init = async function () {
    console.log('PoziAPISearch - init')
    // Model.on('app.search.layer', PoziAPISearch().search)
    Model.on('app.search.complete', searchComplete)

    // Store search query from URL params
    const queryUrlParam = Model.getState('query', '')
    if (queryUrlParam) {
      Model.set('app.search.query', queryUrlParam)
    }

    const layer = getSearchStateLayer()
    if (!layer) {
      console.log('PoziAPISearch - init - no search layer set.')

      return
    }

    // console.debug('PoziAPISearch - init - search layer set.', layer.titleid, layer)

    setSearchLayer(layer)

    const searchValue = Model.get('app.search.query')

    if (!searchValue) {
      console.debug('PoziAPISearch - init - no search query set.', layer.titleid)

      return
    }

    try {
      // Use search zoom map if search layer is a search layer
      const isSearchLayer = layer.type.toLowerCase() === 'search'
      const bZoomMap = isSearchLayer

      await search(bZoomMap)
    } catch (error) {
      const searchValue = Model.get('app.search.query')

      console.log('PoziAPISearch - init - error', layer.titleid, searchValue, error)

      PoziNotifications().warning(`Cannot find ${layer.title} item '${searchValue}'`)
    }
  }

  // getSearchStateLayer is used by things like:
  // * https://app.pozi.com/#/search[propertynumber,123456]
  // * https://app.pozi.com/?propnum=123456 (legacy)
  const getSearchStateLayer = function () {
    const searchState = Model.getState('search', null)
    if (!searchState) {
      console.log('PoziAPISearch - getSearchStateLayer - no search state set.')
      return null
    }

    // The searchKey is usually the titleid (or title) of the search layer
    // that we are interested in.
    // Legacy: it can also be the search field as defined in the config of
    //         a search layer (like 'propNum')
    const searchKey = PoziUtils.getNameOfFirstKey(searchState)

    if (!searchKey) {
      console.log('PoziAPISearch - getSearchStateLayer - no search key set.')
      return null
    }

    // The searchValue is the value of the searchKey
    const searchVal = searchState[searchKey]
    console.debug('PoziAPISearch - getSearchStateLayer - searchKey', searchKey, 'searchVal', searchVal)

    // Store search query in model
    Model.set('app.search.query', searchVal)

    // Find the relevant searchable layer by matching titleid or title
    // let searchLayerConf = searchableLayers.find(layerConf => layerConf.titleid === searchKey || layerConf.title === searchKey)

    // Try to find the layer by titleid
    let searchLayerConf = PoziUtils.getLayerFromTitleID(searchKey)

    // Try the old way by looking for any searchable layers that have a search id field
    // that matches the our searchKey.
    // Note: only these fields are allowed: ['propnum', 'propertypfi', 'parcelpfi', 'parcelspi']
    //       (see relevant code in Model)
    if (!searchLayerConf) {
      const searchableLayers = PoziUtils.getLayersSearchable()
      searchLayerConf = searchableLayers.find((layerConf) => layerConf.search?.id === searchKey)
    }

    if (!searchLayerConf) {
      console.log('PoziAPISearch - getSearchStateLayer - unable to find any searchable layers.', searchKey, searchVal)

      PoziNotifications().warning(`Cannot not find dataset '${searchKey}' to search for value '${searchVal}'`)

      return null
    }

    // console.debug('PoziAPISearch - getSearchStateLayer - layer found', searchLayerConf.titleid, searchLayerConf)

    return searchLayerConf
  }

  /**
   * Set search layer.
   * Use Model.on('app.search.layer', function) for events.
   */
  const setSearchLayer = function (layerConf) {
    console.debug('PoziSearch - setSearchLayer', layerConf)

    if (!layerConf) {
      console.log('PoziAPISearch - setSearchLayer - invalid layer', layerConf)
      return false
    }

    Model.set('app.search.layer', layerConf) // Event kicks off search()
  }

  /**
   * Handle search requests
   */
  const search = function (bZoomMap) {
    // console.debug('PoziSearch.search()', bZoomMap)

    return new Promise(function (resolve, reject) {
      let message = ''
      const layer = Model.get('app.search.layer', null)
      if (!layer) {
        message = 'Layer is not set for search.'
        return reject(message)
      }

      const zoomOnly = layer?.search?.zoomonly

      // const searchType = layer?.search?.type
      // console.debug('PoziAPISearch - zoomMap, zoomonly, searchtype', layer, bZoomMap, zoomonly, searchType)

      // Remove search hash param from URL
      Model.setState('search', null, '')

      if (bZoomMap || zoomOnly) {
        HistoryUpdate()
        zoomSearch(resolve, reject)
      } else {
        autoCompleteSearch(resolve, reject)
      }
    })
  }

  const autoCompleteSearch = function (resolve, reject) {
    let message = ''

    // Work on copy of layerConf as we may be adding search config
    // for layers that don't have any
    const layerConf = cloneDeep(Model.get('app.search.layer', null))

    if (!layerConf) {
      console.log('PoziAPISearch - autoCompleteSearch - no layerConf provided', layerConf)

      message = 'Layer is not set for auto complete search.'
      return reject(message)
    }

    const { titleid, type, searchFields } = layerConf

    let { search = {} } = layerConf

    let searchType = search?.type || ''

    // Set search type to pozimaplayer for non-search layers
    const isSearchLayer = type.toLowerCase() === 'search'
    if (!isSearchLayer) {
      if (!searchType) {
        // console.debug('PoziAPISearch - autoCompleteSearch - no search config. Creating one!', titleid)

        searchType = 'pozimaplayers'
        search = {
          type: searchType,
          ...search
        }

        if (Array.isArray(searchFields)) {
          searchType = 'pozisearchfields'
          search = {
            type: searchType,
            ...search
          }
        }

        layerConf.search = search
      }
    }

    if (isEmpty(search)) {
      console.log('PoziAPISearch - autoCompleteSearch - no search config', titleid, isSearchLayer, searchType, search, layerConf.search)

      message = 'Unable to find search config for layerid ' + titleid
      return reject(message)
    }

    // Early escape for coordinate searches as they do not retrieve data from a URL endpoint
    if (searchType === 'coordinate') {
      return resolve(parseCoordinateResponse(layerConf))
    }

    let url = layerConf.config?.spatial?.url
    if (!url) {
      message = 'Unable to find search config url for layerid ' + titleid

      console.log('autoCompleteSearch - no url found in config', titleid, layerConf)

      return reject(message)
    }

    const params = layerConf.config?.spatial?.params
    if (params) {
      const paramsStr = PoziUtils.paramsToUrl(params) || ''
      if (paramsStr !== '') {
        url += url.includes('?') ? '&' + paramsStr : '?' + paramsStr
      }
    }

    // Create CQL expression for search if searchFields is defined
    if (layerConf.searchFields && layerConf.search?.field) {
      const searchField = layerConf.search.field

      const expFilter = `${searchField} ILIKE '%(searchquery)%'`
      const expFilterEncoded = encodeURIComponent(expFilter)

      url += (url.includes('?') ? '&' : '?') + `EXP_FILTER=${expFilterEncoded}&SORTBY=${searchField}&MAXFEATURES=${SEARCH_MAX_WFS_FEATURES}`

      // console.debug('autoCompleteSearch - url', url)
    }

    const bbox = search.bbox
    if (bbox) {
      const bboxHereFormat = bbox[0][1] + ',' + bbox[0][0] + ';' + bbox[1][1] + ',' + bbox[1][0]
      const bboxHereV7Format = bbox[0][0] + ',' + bbox[1][1] + ',' + bbox[1][0] + ',' + bbox[0][1]
      url = url.replace('[bboxhereformat]', bboxHereFormat)
      url = url.replace('[bboxherev7format]', bboxHereV7Format)
      url = url.replace('[min_lat]', Math.min(bbox[0][1], bbox[1][1]))
      url = url.replace('[max_lat]', Math.max(bbox[0][1], bbox[1][1]))
      url = url.replace('[min_lon]', Math.min(bbox[0][0], bbox[1][0]))
      url = url.replace('[max_lon]', Math.max(bbox[0][0], bbox[1][0]))
    } else {
      url = url.replace('&bbox=[bboxhereformat]', '')
      url = url.replace('&bbox=[bboxherev7format]', '')
      url = url.replace('[min_lat]', '')
      url = url.replace('[min_lon]', '')
      url = url.replace('[max_lat]', '')
      url = url.replace('[max_lon]', '')
    }

    const parseResponse = (data) => {
      // console.debug('parseResponse - Search parse response data', titleid, searchType, url, data)

      if (!data) {
        console.log('PoziAPISearch - autoCompleteSearch - parseResponse - No data in response.')
        return []
      }

      switch (searchType) {
        case 'ckanapi':
          return parseCKANAPIResponse(data, layerConf)
        case 'here':
          return parseHereResponse(data, layerConf)
        case 'hereV7':
          return parseHereResponseV7(data, layerConf)
        case 'mapshare':
          return parseMapShareResponse(data, layerConf)
        case 'mapzen':
          return parseMapzenResponse(data, layerConf)
        case 'pozisearch':
          return parsePoziSearchResponse(data, layerConf)
        case 'pozimaplayers':
          return parseMapLayerResponse(data, layerConf)
        default:
          return parseGeoJSONResponse(data, layerConf)
      }
    }

    const sendRequest = (urlOrig) => {
      // console.debug('PoziAPISearch - sendRequest - urlOrig', urlOrig)

      let url = urlOrig
      url = searchReplaceSearchQuery(url)
      url = searchReplaceLGACode(url)
      url = searchReplaceMapXY(url)

      // console.debug('sendRequest - Request new URL', url)

      // Don't send a request if we have the results already
      const cachedResponse = resultsCache[url]
      if (cachedResponse) {
        // console.debug('PoziAPISearch - Returning cached result for url', url)
        return resolve(cachedResponse)
      }

      PoziAPIData()
        .ajax({
          url,
          error: (error) => {
            console.log('PoziAPISearch - sendRequest - ajax error', url, error)

            return reject(error)
          },
          success: (response) => {
            const parsedData = parseResponse(response)

            // Store results in cache
            resultsCache[url] = parsedData

            return resolve(parsedData)
          }
        })
        .catch((error) => {
          console.log('PoziAPISearch - sendRequest - unexpected error', error)
          return reject(error)
        })
    }

    const query = Model.get('app.search.query', null)
    if (query) {
      // pozimaplayers
      if (searchType === 'pozimaplayers') {
        console.debug('PoziAPISearch - type is pozimaplayers', titleid, layerConf.selected, query)

        return loadAndActivateVectorLayer(layerConf)
          .then((geojson) => {
            console.debug('PoziAPISearch - pozimaplayers - data loaded', titleid)
            const parsedData = parseResponse(geojson)

            return resolve(parsedData)
          })
          .catch((error) => {
            console.error('Search - pozimaplayers - error loading data', error)
            return reject(error)
          })
      }

      return sendRequest(url)
    }
  }

  const getFeatureGeomProps = (feature) => {
    // console.debug('getFeatureGeomProps', feature)

    if (!(feature instanceof OLFeature)) {
      // console.debug('getFeatureGeomProps - feature not GeoJSON. Attempting to convert to GeoJSON', feature)

      // Allow for any kind of geometry (and collection) and get the center point of its bounding box
      const features = new OLFormatGeoJSON().readFeatures(feature)
      feature = features instanceof Array && features[0]
    }

    if (!feature) {
      console.warn('getFeatureGeomProps - No or invalid feature.', feature)
      return null
    }

    const geometry = feature.getGeometry()

    // Handle case where feature has a property called 'geometry' that actually is not
    // an OL geometry
    const featureHasGeom = geometry && typeof geometry.getType === 'function'

    // Try to get BBOX from properties for point features or features without a geometry
    let bbox
    const geomIsPoint = featureHasGeom && geometry.getType() === 'Point'
    if (geomIsPoint || !featureHasGeom) {
      const properties = feature.getProperties() || {}
      const { maxx, maxy, minx, miny } = properties
      if (properties.bbox) {
        bbox = properties.bbox
      } else if (minx && miny && maxx && maxy) {
        bbox = [minx, miny, maxx, maxy]
      }
    } else {
      bbox = featureHasGeom && geometry.getExtent()
    }

    // Calculate point on surface ('center' point)
    let xy
    if (geomIsPoint) {
      xy = geometry.getCoordinates()
    } else if (featureHasGeom) {
      const geojson = new OLFormatGeoJSON().writeFeatureObject(feature)
      const buffered = buffer(geojson, -0.0001, { units: 'kilometers' }) || geojson
      const pointOnSurface = pointOnFeature(buffered)
      xy = pointOnSurface?.geometry?.coordinates

      // console.debug('getFeatureGeomProps - get xy from geom.', feature, xy)
    } else if (bbox) {
      xy = getCenter(bbox)

      // console.debug('getFeatureGeomProps - get xy from bbox.', feature, bbox, xy)
    } else {
      console.debug('getFeatureGeomProps - cannot calculate xy.', feature)
    }

    const geomProps = {
      bbox,
      geometry,
      xy
    }

    // console.debug('getFeatureGeomProps - geom props', geomProps)

    return geomProps
  }

  /**
   * parseMapLayerResponse
   * @param {data} Object GeoJSON formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parseMapLayerResponse = function (data, layerConf) {
    const { titleid, type } = layerConf

    // console.debug('parseMapLayerResponse', titleid, data)

    // Model.set('app.search.geojson', data) // Must be geojson

    const searchItems = []

    const labelField = layerConf?.config?.spatial?.label || ''
    const searchIdField = layerConf?.config?.spatial?.id ?? ''

    // console.debug('parseMapLayerResponse - labelField/searchIdField', titleid, labelField, searchIdField)

    const features = get(data, 'features', [])

    const query = Model.get('app.search.query', '').toLowerCase().trim()

    if (!query) {
      console.log('parseMapLayerResponse - Empty query string.', titleid)
      return searchItems
    }

    const maxFeatures = 0 //  Set to 0 to return all
    const numFeatures = features.length

    let featureCount = 0
    for (const feature of features) {
      const featureProperties = feature.properties

      if (!featureProperties) {
        continue
      }

      // console.debug('parseMapLayerResponse - value', titleid, labelFieldValue, feature)

      const matchingProperties = {}
      for (const [field, value] of Object.entries(featureProperties)) {
        // Skip autogenerated fields
        if (field.startsWith('$')) {
          continue
        }

        const match = matchQuery(value, query)

        if (match) {
          matchingProperties[field] = cleanupString(value) || ''
        }
      }

      // console.debug('parseMapLayerResponse - matchingProperties', titleid, matchingProperties)

      // Skip if no matching properties are found
      if (isEmpty(matchingProperties)) {
        continue
      }

      // This ranks and sorts the properties by rank
      const matchingPropertiesRanked = Label.getStringProperties(matchingProperties)

      // console.debug('parseMapLayerResponse - matchingPropertiesRanked', titleid, matchingPropertiesRanked)

      const bestMatchingProperty = matchingPropertiesRanked[0]
      const { name } = bestMatchingProperty
      let { value } = bestMatchingProperty

      // We will need the raw value for the sorting of the results by length, where 'value'
      // may have the label field value appended
      const rawValue = value

      // Make category present nicer
      const category = String(name).toUpperCase().replace('_', ' ')

      // If the field is not the label field, add the value of that as a tooltip
      const hasLabelFieldValue = labelField && !isNil(featureProperties[labelField])
      const labelFieldValue = hasLabelFieldValue && cleanupString(featureProperties[labelField])

      // console.debug('search - parseMapLayerResponse - hasLabelFieldValue', titleid, labelField, hasLabelFieldValue, featureProperties)

      // If the field is not the label field, add the value of that to the value
      if (category !== labelField && hasLabelFieldValue) {
        const valueMatchesLabelFieldValue = value.toLowerCase() === labelFieldValue.toLowerCase()
        if (!valueMatchesLabelFieldValue) {
          value = `${value}  |  ${labelFieldValue}`
        }
      }

      let idField = searchIdField

      // Get the value of the ID field if it exists
      let id = (searchIdField && feature.properties?.[searchIdField]) ?? null
      if (!id) {
        id = GetPropertiesId(layerConf, feature.properties)

        // console.debug('parseMapLayerResponse - missing id. Found?', titleid, searchIdField, id)
      }

      if (!idField) {
        idField = GetPropertiesIdKey(layerConf, feature.properties)
      }

      const geomProps = getFeatureGeomProps(feature)

      searchItems.push({
        ...geomProps,

        category,
        id,
        idField,
        rawValue,
        value
      })

      featureCount++

      // Stop looping through features if we have reached the maximum number of results
      if (maxFeatures > 0 && featureCount >= maxFeatures) {
        break
      }
    }

    searchItems.sort(buildIntuitiveSearchResultSorterBy('rawValue', query))

    // Inform user when we have reached the maximum number of results
    if (maxFeatures > 0 && featureCount >= maxFeatures && featureCount < numFeatures) {
      searchItems.push({
        id: `${titleid}-limit-reached`,
        disabled: true,
        category: `showing first ${maxFeatures} results`,
        value: `-- limit reached -- (${query})`
      })
    }

    // console.debug('parseMapLayerResponse - searchItems', titleid, searchItems)

    return searchItems
  }

  /**
   * parseGeoJSONResponse
   * @param {data} Object GeoJSON formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parseGeoJSONResponse = function (geojson, layerConf) {
    const { titleid, type } = layerConf

    // If any transformers are defined, apply them. This is used for reordering the fields
    // when data has been returned from QGIS WFS for example.
    const hasTransformers = layerConf.config?.transformer
    if (hasTransformers) {
      geojson = ApplyTransformers(layerConf, geojson)
    }

    // console.debug('Parse geojson response.', titleid, geojson)

    Model.set('app.search.geojson', geojson) // Must be geojson

    const searchItems = []

    const labelField = layerConf?.config?.spatial?.label
    const searchField = layerConf?.search?.field
    // console.debug('Parse geojson response - searchField', layerConf, searchField, labelField)
    if (!searchField && !labelField) {
      console.error('Either a searchField (searchFields keyword) or a labelField (config.spatial.label) must be defined', layerConf)
      return searchItems
    }

    // console.debug('Parse geojson response - searchField', titleid, searchField)

    const titlecase = layerConf?.search?.titlecase ?? false
    const features = geojson?.features || []
    const useFullTextSearch = layerConf?.search?.fulltext ?? true

    const query = Model.get('app.search.query', null)
    if (!query) {
      console.log('Empty query string.')
      return searchItems
    }

    for (const feature of features) {
      const searchFieldValue = String(feature?.properties?.[searchField] ?? '')
      const labelFieldValue = cleanupString(feature?.properties?.[labelField]) ?? ''
      // For custom WFS searches, the search field may not be defined in the configuration
      let value = searchFieldValue || labelFieldValue
      if (searchFieldValue && labelFieldValue && searchFieldValue.toLowerCase() !== labelFieldValue.toLowerCase()) {
        value = `${searchFieldValue}  |  ${labelFieldValue}`
      }

      // console.debug('Parse geojson response - value', titleid, value, feature)

      if (!value || !query) {
        continue
      }

      // By default use the Search AutoComplete's full text search logic.  If fulltext is explicitly set as false in the
      // site config, then test whether the search text is within the search field text.  If not, exclude from results.
      const valueToSearch = searchFieldValue || labelFieldValue
      if (!useFullTextSearch && valueToSearch) {
        const queryInSearchField = valueToSearch.toLowerCase().includes(query.toLowerCase())
        if (!queryInSearchField) {
          continue
        }
      }

      if (titlecase) {
        value = PoziUtils.toTitleCase(value)
      }

      const geomProps = getFeatureGeomProps(feature)

      searchItems.push({
        ...geomProps,

        map_layer: layerConf,
        feature,
        value
      })
    }

    searchItems.sort(buildIntuitiveSearchResultSorterBy('value', query))

    // console.debug('Parse geojson response - searchItems', titleid, searchItems)

    return searchItems
  }

  /**
   * parseCKANAPIResponse
   * @param {data} Object CKAN API JSON formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parseCKANAPIResponse = function (data, layerConf) {
    console.debug('Parse ckanapi response.')
    const geojson = { type: 'FeatureCollection', features: [] }
    const label = layerConf?.config?.spatial?.label ?? ''
    const features = data?.result?.records || []
    for (const key in features) {
      const feature = features[key]
      const value = feature?.[label]
      const x = parseFloat(feature?.x)
      const y = parseFloat(feature?.y)
      if (!x || !y || !value) {
        continue
      }
      const point = {
        type: 'Feature',
        properties: { display: value },
        geometry: { type: 'Point', coordinates: [x, y] }
      }
      geojson.features.push(point)
    }
    return parseGeoJSONResponse(geojson, layerConf)
  }

  /**
   * Parse coordinate response
   * @param {Object} layerConf Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   *
   * If the search query entered is a coordinate and it is within the bounding box (if supplied), return the
   * coordinates to the autocomplete search
   */
  const parseCoordinateResponse = (layerConf) => {
    // Get the search query entered
    const searchQuery = Model.get('app.search.query', null)
    const bbox = layerConf.search?.bbox

    // Split the search query into tokens using valid delimiters
    const tokeniseInput = (input) => input.trim().replace(/[,;]+/g, ' ').split(/\s+/)

    const isValidBbox = (bbox) => Array.isArray(bbox) && bbox.length === 2 && bbox.every((coord) => Array.isArray(coord) && coord.length === 2)

    // Function to determine if the search query entered is a coordinate.
    const parseCoordinates = (input, bbox) => {
      const tokens = tokeniseInput(input)
      // Only accepts 2 or 3 delimited inputs
      if (tokens.length !== 2 && tokens.length !== 3) {
        return false
      }

      let lon, lat, projection, x, y

      // Case 1: User has entered a coordinate in the form of "EPSG:xxxx X Y"
      if (tokens.length === 3) {
        const epsg = tokens[0].toUpperCase()
        if (!epsg.startsWith('EPSG:') || !isSupportedProjection(epsg)) {
          console.error(`Invalid input or unsupported coordinate system: ${epsg}`)
          PoziNotifications().warning(`Invalid input or unsupported coordinate system: ${epsg}`)
          return false
        }

        x = parseFloat(tokens[1])
        y = parseFloat(tokens[2])
        if (isNaN(x) || isNaN(y)) {
          return false
        }

        projection = supportedProjections[epsg].name
        try {
          ;[lon, lat] = proj4(epsg, DEFAULT_PROJECTION_CODE, [x, y])
        } catch {
          console.error(`Error reprojecting coordinates: ${epsg} to WGS84`, [x, y])
          PoziNotifications().warning(`Error reprojecting coordinates: ${epsg} to WGS84`)
          return false
        }
        // Case 2: Assume WGS84 long/lat coordinates
      } else if (tokens.length === 2) {
        projection = 'Lon/Lat'
        const coord1 = parseFloat(tokens[0])
        const coord2 = parseFloat(tokens[1])
        if (isNaN(coord1) || isNaN(coord2)) return false

        // Check coordinates are valid. Default order is lon/lat, but switch if definitely lat/lon
        // Absolute value of lat must be less than or equal to 90
        lon = Math.abs(coord2) > 90 ? coord2 : coord1
        lat = Math.abs(coord2) > 90 ? coord1 : coord2

        if (Math.abs(lon) > 180 || Math.abs(lat) > 90) {
          console.error(`Invalid coordinates: ${lon}, ${lat}`)
          PoziNotifications().warning(`Invalid coordinates: ${lon}, ${lat}`)
          return false
        }
      }

      if (isValidBbox(bbox)) {
        const [[x1, y1], [x2, y2]] = bbox
        const [minLon, maxLon] = [Math.min(x1, x2), Math.max(x1, x2)]
        const [minLat, maxLat] = [Math.min(y1, y2), Math.max(y1, y2)]

        if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) {
          console.error(`Coordinates outside search bounding box: ${lon}, ${lat}`, bbox)
          PoziNotifications().warning(`Coordinates outside search bounding box: ${lon}, ${lat}`)
          return false
        }
      }

      return { lat, lon, projection, x, y }
    }

    const position = parseCoordinates(searchQuery, bbox)
    if (!position) return []

    return [
      {
        value: position.projection + ': ' + (position.x || position.lon) + ', ' + (position.y || position.lat),
        xy: [position.lon, position.lat]
      }
    ]
  }

  /**
   * parseHereResponse
   * @deprecated v6.2 DEPRECATED July 2025 - Remove function once it is clear that it will no longer be needed
   * @param {data} Object Here formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parseHereResponse = function (data, layerConf) {
    console.debug('Parse Here data response.', data, layerConf)
    if (!data.Response || !data.Response.View) {
      console.error('Unable to parse here data format.', layerConf)
      return []
    }

    if (data.Response.View.length === 0) {
      console.log('No data found in here data results.')
      return []
    }

    if (!data.Response.View[0] || !data.Response.View[0].Result) {
      console.error('Unable to parse here data format.', layerConf)
      return []
    }

    const result = get(data, 'Response.View[0].Result', [])

    // Sort the result
    // if (result.length > 0) {
    //   data.Response.View[0].Result = sortBy(result, [
    //     function (o) {
    //       return get(o, 'Location.Address.Label', '')
    //     }
    //   ])
    // }

    const bbox = layerConf.search?.bbox

    return (
      $.map(result, function (feat) {
        const location = feat.Location
        const displayPosition = location.DisplayPosition

        let isInside = true // Add search result by default. Exclude if outside bounding box, if defined.
        if (bbox) {
          const minLongitude = Math.min(bbox[0][0], bbox[1][0])
          const maxLongitude = Math.max(bbox[0][0], bbox[1][0])
          const minLatitude = Math.min(bbox[0][1], bbox[1][1])
          const maxLatitude = Math.max(bbox[0][1], bbox[1][1])
          const searchLongitude = displayPosition.Longitude
          const searchLatitude = displayPosition.Latitude

          if (searchLongitude < minLongitude || searchLongitude > maxLongitude || searchLatitude < minLatitude || searchLatitude > maxLatitude) {
            isInside = false
          }
        } else {
          console.log('parseHereResponse - Warning: Unable to get bbox.')
        }

        if (isInside) {
          let address = location.Address.Label || ''

          if (address) {
            address = PoziUtils.sanitiseAddress(address)
          } else {
            // Use the coordinates instead
            address = `Lon/Lat: ${displayPosition.Longitude}, ${displayPosition.Latitude}`
          }

          return {
            value: address,
            xy: [displayPosition.Longitude, displayPosition.Latitude]
          }
        }
      }) || []
    )
  }

  const parseHereResponseV7 = (data, layerConf) => {
    console.debug('Parse HERE v7 data response.', data, layerConf)
    if (!data?.items || !Array.isArray(data.items)) {
      console.error('Unable to parse HERE data format: missing or invalid "items" array.', layerConf, data)
      return []
    }

    if (data.items.length === 0) {
      console.log('No data found in HERE data results.')
      return []
    }

    if (!data.items[0]?.address || !data.items[0]?.position) {
      console.error('Unable to parse HERE data format: missing attributes in first item.', layerConf, data.items[0])
      return []
    }

    return data.items
      .map((feat) => {
        const displayPosition = feat.position
        const address = PoziUtils.sanitiseAddress(feat.address?.label)

        return {
          value: address,
          xy: [displayPosition.lng, displayPosition.lat]
        }
      })
      .filter(Boolean)
  }

  /**
   * parseMapShareResponse
   * @param {data} Object MapShare formatted data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} Array of functions that return {Value/XY} objects when called
   */
  const parseMapShareResponse = function (data, layerConf) {
    console.debug('Parse MapShare data response.', data, layerConf)

    const results = data && data.suggestions

    if (!results || !(results instanceof Array)) {
      console.error('Error returned by mapshare.', layerConf, data)
      return []
    }

    if (results.length === 0) {
      console.log('No candidates found in mapshare data results.')
      return []
    }

    if (!results[0] || !results[0].text || !results[0].magicKey) {
      console.error('Unable to parse mapshare data format.', layerConf)
      return []
    }

    // forwardSearchUrl for fetching extra details like location's coordinates
    const forwardSearchUrl = layerConf?.config?.spatial?.forwardSearchUrl
    if (!forwardSearchUrl) {
      console.error('MapsShare forwardSearchUrl is not present in config.')
      return []
    }

    // Sort the results
    // results = sortBy(results, [
    //   function (o) {
    //     return get(o, 'text', '')
    //   }
    // ])

    // Every suggestion will be a function that gets called when it gets selected. That
    // function will request the necessary details to satisfy the search requirements (like
    // the coordinates of the resulting location).
    return $.map(results, (result) => {
      // Match and replace any variables in URL agains the result object
      // e.g. [magicKey] and [text] will be replaced by result.magicKey and result.text
      const url = PoziUtils.replaceUrlVariablesWithPropsValues(forwardSearchUrl, result)

      let address = get(result, 'text', 'Unnamed location')
      address = PoziUtils.sanitiseAddress(address)

      // 'suggestion' is an async function that'll request and return the address details when search
      // is complete. This function will be called in the getResults function
      const suggestion = async () => {
        // Request address details
        const data = await PoziAPIData().ajaxAsPromise({ url })

        // There should be one and only one result
        const candidate = get(data, 'candidates', [])[0]
        const locationX = get(candidate, 'location.x', null)
        const locationY = get(candidate, 'location.y', null)

        if (!locationX || !locationY) {
          console.error(`No coordinates found for '${address}'`, candidate, data, url)
          throw new Error(`No coordinates found for '${address}'`)
        }

        return {
          value: address,
          xy: [locationX, locationY]
        }
      }

      // The following will be used as the autocomplete results label.
      // This is a bit of a hack by treating the function as an object
      suggestion.value = address

      return suggestion
    })
  }

  /**
   * parseMapzenResponse
   * @param {data} Object Mapzen formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parseMapzenResponse = function (data, layerConf) {
    // console.debug('Parse Mapzen data response.', data, layer)

    if (data.geocoding && data.geocoding.errors) {
      console.error('Error returned by mapzen.', layerConf, data.geocoding.errors)
      return []
    }

    if (data.features.length === 0) {
      console.log('No features found in mapzen data results.', data)
      return []
    }

    if (!data.features[0] || !data.features[0].properties || !data.features[0].properties.label) {
      console.error('Unable to parse mapzen data format.', layerConf)
      return []
    }

    // Sort the features
    // const features = get(data, 'features', [])
    // if (features.length > 0) {
    //   data.features = sortBy(features, [
    //     function (o) {
    //       return get(o, 'label', '')
    //     }
    //   ])
    // }

    /*
      You can add these to the site configs like this:
      "drawer": {
      "tabs": {
        "search": {
          "mapzen": {
            "discard": [{ "prop": "street", "value": "Yirrill Way" }]
          }
        },
        ...
      These are the options for mapzen:
        accuracy: "point"
        continent: "Oceania"
        continent_gid: "whosonfirst:continent:102191583"
        country: "Australia"
        country_a: "AUS"
        country_gid: "whosonfirst:country:85632793"
        county: "Greater Bendigo"
        county_a: "GB"
        county_gid: "whosonfirst:county:102049661"
        gid: "openaddresses:address:au/vic/statewide:c7606d29bf1123e4"
        housenumber: "8"
        id: "au/vic/statewide:c7606d29bf1123e4"
        label: "8 Yirrilil Way, Strathfieldsaye, VIC, Australia"
        layer: "address"
        localadmin: "Strathfieldsaye"
        localadmin_gid: "whosonfirst:localadmin:404540331"
        name: "8 Yirrilil Way"
        postalcode: "3551"
        region: "Victoria"
        region_a: "VIC"
        region_gid: "whosonfirst:region:85681497"
        source: "openaddresses"
        source_id: "au/vic/statewide:c7606d29bf1123e4"
        street: "Yirrilil Way"
    */
    const discardProps = Model.get('config.layout.drawer.tabs.search.mapzen.discard', [])
    if (discardProps.length > 0) {
      remove(data.features, function (feat) {
        const props = feat.properties
        let discard = false
        forOwn(discardProps, function (dObj, key) {
          if (props[dObj.prop] === dObj.value) {
            discard = true
            return false // exit loop
          }
        })
        return discard
      })
    }

    return $.map(data.features, function (feat) {
      let address = feat.properties.label
      address = PoziUtils.sanitiseAddress(address)

      const data = {
        value: address,
        xy: [feat.geometry.coordinates[0], feat.geometry.coordinates[1]]
      }

      return data
    })
  }

  /**
   * parsePoziSearchResponse
   *
   * Pozi Unified Search
   *
   * @param {data} Object PoziSearch formated data structure
   * @param {Object} Layer Pozi layer config
   * @returns {Array} {Value/XY} Objects.
   */
  const parsePoziSearchResponse = (data, layerConf) => {
    console.debug('Parse PoziSearch data response', data, layerConf)

    if (data.geocoding && data.geocoding.errors) {
      console.error('Error returned by PoziSearch', layerConf, data.geocoding.errors)
      return []
    }

    const features = data?.features

    if (!features) {
      console.log('Error: invalid response from PoziSearch')
      return []
    }

    if (features.length === 0) {
      console.log('No features found in PoziSearch data results')
      return []
    }

    const firstFeatureProps = features[0]?.properties
    if (!firstFeatureProps?.display_text && !firstFeatureProps?.search_text) {
      console.error("Unable to parse PoziSearch data format: 'display_text' and 'search_text' fields missing.", layerConf, firstFeatureProps, features)
      return []
    }

    // forwardSearchUrl for fetching extra details like location's coordinates
    const forwardSearchUrl = layerConf?.config?.spatial?.forwardSearchUrl

    const generateDataFromResult = (result) => {
      if (!result) {
        console.warn('generateDataFromResult - No result provided', result)
        return null
      }

      // Allow for any kind of geometry (and collection) and get the center point of its bounding box
      const features = new OLFormatGeoJSON().readFeatures(result)
      const feature = features instanceof Array && features[0]

      if (!feature) {
        console.warn('generateDataFromResult - No or invalid features from result', result, features)
        return null
      }

      const geomProps = getFeatureGeomProps(feature)
      const properties = feature.getProperties() || {}

      const label = String(properties.display_text ?? properties.search_text ?? '<DISPLAY_TEXT_MISSING>')

      // console.debug('generateDataFromResult - label:', label, properties)

      const value = PoziUtils.sanitiseAddress(label)

      const data = {
        ...geomProps,

        category: properties.category,
        id: properties.map_layer_id,
        map_layer: properties.map_layer,
        value
      }

      const forwardSearchKey = properties.forward_search_key
      if (!forwardSearchKey) {
        // console.debug('Search: forward search key not provided. That is OK.', feature)
        return data
      }

      console.log('Search: forward search key supplied:', forwardSearchKey)

      if (!forwardSearchUrl) {
        console.error('PoziSearch forwardSearchUrl is not present in config.')
        return data
      }

      // Match and replace any variables in URL agains the result object
      // e.g. [forward_search_key] will be replaced by the value of that key
      const url = PoziUtils.replaceUrlVariablesWithPropsValues(forwardSearchUrl, properties)

      // 'suggestion' is an async function that'll request and return the address details when search
      // is complete. This function will be called in the getResults function
      const suggestion = async () => {
        console.debug('PoziAPISearch - Running forward search', properties, url)
        // console.trace()
        try {
          // Request further details using forward search
          const forwardSearchResult = await PoziAPIData().ajaxAsPromise({ url })

          const forwardSearchData = generateDataFromResult(forwardSearchResult)

          const combinedData = {
            ...data,
            ...forwardSearchData
          }

          return combinedData
        } catch (error) {
          console.warn('Search - Forward search failed: ', { ...error })
          return data
        }
      }

      // The following will be used as the autocomplete results label.
      // This is a bit of a hack by treating the function as an object
      suggestion.value = value
      suggestion.category = properties.category

      return suggestion
    }

    return $.map(data.features, function (result) {
      const data = generateDataFromResult(result)

      // console.debug('Features map', layerConf.titleid, result, data)

      return data
    })
  }

  /**
   * Zoom search. E.g. propnum, propertypfi, etc.
   */
  const zoomSearch = function (resolve, reject) {
    let message = ''

    const layer = Model.get('app.search.layer', null)
    if (!layer) {
      message = 'Layer is not set for zoom search.'
      return reject(message)
    }

    const config = layer?.search
    if (!config) {
      message = 'Unable to find search config for layer' + layer.titleid
      return reject(message)
    }

    // const bbox = get(layer, 'search.bbox', null)
    // const bboxHereFormat = bbox[0][1] + ',' + bbox[0][0] + ';' + bbox[1][1] + ',' + bbox[1][0]

    let url = PoziUtils.getLayerUrl(layer)

    if (!url) {
      message = 'Unable to find search config endpoint for layerid ' + layer.titleid
      return reject(message)
    }

    url = searchReplaceSearchQuery(url)
    url = searchReplaceLGACode(url)

    const query = Model.get('app.search.query', null)
    if (!query) {
      message = 'Empty search query for layerid ' + layer.titleid
      return reject(message)
    }

    // console.debug('PoziAPISearch url', url)

    return getDataAndZoom(url, resolve, reject)
  }

  const getDataAndZoom = function (url, resolve, reject) {
    // console.debug('getDataAndZoom', url)

    PoziAPIData()
      .ajax({
        url,
        error: reject,
        success: function (geojson) {
          // console.debug('getDataAndZoom - success - geojson', url, geojson)

          // If we have multiple results, activate the search tab and show those results
          const numResults = geojson?.features?.length ?? 0
          if (numResults === 0) {
            console.log('getDataAndZoom - no results', url, geojson)

            return reject('Search has no results.')
          }

          if (numResults > 1) {
            console.debug('getDataAndZoom - multiple results', geojson.features.length)

            Model.set('app.drawer.activetabid', CONFIG_DRAWER_TAB_ACTIVE_SEARCH)

            return resolve('Search has multiple results. Showing search panel')
          }

          // If we end up here, this are either no results or only one
          const zoomPadding = 0.001
          const bSelectCenterPoint = true

          PoziZoom().zoomMap(geojson, zoomPadding, bSelectCenterPoint, resolve, reject)
        }
      })
      .catch((error) => {
        console.debug('PoziAPISearch - getDataAndZoom - ajax error', url, error)
      })
  }

  const searchReplaceSearchQuery = function (url) {
    if (!includes(url, '(searchquery)')) {
      return url
    }

    let searchquery = Model.get('app.search.query', '')

    if (!searchquery) {
      console.log('No search query found, replace with empty string?')
    }

    // This addresses an issue with the way GeoServer handles LIKE queries.
    url = PoziUtils.replaceAll(url, '%(searchquery)%', '%25(searchquery)%25')

    // Very basic check to see if the query uses the CQL_FILTER LIKE/ILIKE style which needs double backslashes in querystring.
    if (includes(url, 'CQL_FILTER') && includes(url, 'LIKE')) {
      // This addresses an issue with the way GeoServer handles LIKE/ILIKE queries.
      const singleBackslash = '\\'
      const doubleBackslash = '\\\\'
      if (includes(searchquery, singleBackslash)) {
        searchquery = PoziUtils.replaceAll(searchquery, singleBackslash, doubleBackslash)
      }
    }

    // Make QGIS (I)LIKE queries more robust by replacing spaces with wildcards (%)
    const urlLC = url.toLowerCase()
    if (urlLC.includes('exp_filter') && urlLC.includes('like')) {
      searchquery = searchquery.replace(/\W+/g, '%')
    }

    if (url.includes("='(searchquery)'")) {
      const searchqueryArray = searchquery.split(',')
      let searchqueryString = ''
      for (const key in searchqueryArray) {
        const value = searchqueryArray[key]
        searchqueryString += `'${value}',`
      }
      searchqueryString = searchqueryString.slice(0, -1) // Remove last comma
      url = PoziUtils.replaceAll(url, "='(searchquery)'", ' IN (' + encodeURIComponent(searchqueryString) + ')')
    }

    if (url.includes('(searchquery)')) {
      url = PoziUtils.replaceAll(url, '(searchquery)', encodeURIComponent(searchquery))
    }

    return url
  }

  const searchReplaceLGACode = function (url) {
    if (!includes(url, '(lgacode)')) {
      return url
    }

    let lgacode = Model.getState('lgacode', null)
    if (!lgacode) {
      lgacode = Model.get('config.LGA', null)
    }
    if (lgacode && lgacode !== '' && lgacode > 0) {
      console.log('Update url endpoint with lgacode', lgacode)
      url = PoziUtils.replaceAll(url, '(lgacode)', lgacode)
    }
    return url
  }

  // Replace mapX and mapY URL variables with the current map center coordinates
  const searchReplaceMapXY = function (url) {
    // console.debug('searchReplaceMapXY - url', url)

    if (url.includes('[mapX]') && url.includes('[mapY]')) {
      let mapX = Model.getState('x', null)
      let mapY = Model.getState('y', null)

      // In case there is no mapX and mapY (e.g. when running in widget mode),
      // try using the site's map display bounding box instead
      if (!mapX && !mapY) {
        const mapDisplayBbox = Model.get('config.layout.map.views.bbox', null)
        // console.debug('searchReplaceMapXY - mapDisplayBbox', mapDisplayBbox)

        if (mapDisplayBbox && mapDisplayBbox.length === 2) {
          // Pozi's map display bounding box is [top-left, bottom-right], resulting
          // in [[minX, maxY], [maxX, minY]], which needs to be converted to [minX, minY, maxX, maxY]

          const [[minX, maxY], [maxX, minY]] = mapDisplayBbox

          // console.debug('searchReplaceMapXY - minX, minY, maxX, maxY', minX, minY, maxX, maxY)

          const center = getCenter([minX, minY, maxX, maxY])
          mapX = center[0]
          mapY = center[1]

          // console.debug('searchReplaceMapXY - mapX, mapY', mapX, mapY)
        }
      }

      if (mapX && mapY) {
        url = url.replace('[mapX]', mapX)
        url = url.replace('[mapY]', mapY)
      }
    }

    return url
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziAPISearch
